import { useNavigate } from "react-router-dom";
import { PERSONA_LABELS, type Persona } from "@velivo/shared";

/**
 * FR-DASH-05 — Stakeholder Persona Sentiment.
 *
 * A horizontal bar per persona, showing how well each stakeholder's questions
 * are being answered. Each bar is clickable through to the underlying prompts
 * and responses, per the FRD's AC — a persona breakdown you can't open is a
 * chart, not a diagnostic.
 */

export interface PersonaRow {
  persona: Persona;
  samples: number;
  mptr: number | null;
  ndi: number | null;
  alignment: number | null;
  severity: "aligned" | "erosion" | "critical" | null;
}

export function PersonaSentiment({
  data,
  pillarId,
}: {
  data: PersonaRow[] | null;
  pillarId: string;
}) {
  const navigate = useNavigate();

  if (!data) {
    return (
      <div className="skeleton-stack">
        {[0, 1, 2].map((i) => (
          <div className="skeleton" key={i} style={{ height: 40 }} />
        ))}
      </div>
    );
  }

  const measured = data.filter((p) => p.samples > 0);
  if (measured.length === 0) {
    return (
      <p className="note-empty">
        No persona has a scored response in this period. Approve prompts for a
        persona and run a capture to see how that stakeholder&rsquo;s questions
        are answered.
      </p>
    );
  }

  return (
    <ul className="personas">
      {data.map((row) => {
        const disabled = row.samples === 0;
        return (
          <li className="persona" key={row.persona}>
            <button
              type="button"
              className="persona__row"
              disabled={disabled}
              onClick={() =>
                navigate(
                  `/prompts?persona=${row.persona}${pillarId ? `&pillarId=${pillarId}` : ""}`,
                )
              }
              aria-label={
                disabled
                  ? `${PERSONA_LABELS[row.persona]}: not measured in this period`
                  : `${PERSONA_LABELS[row.persona]}: pull-through ${row.mptr}%, drift ${row.ndi}, ${row.samples} responses`
              }
            >
              <span className="persona__name">
                {PERSONA_LABELS[row.persona]}
              </span>
              <span className="persona__track">
                {disabled ? (
                  // Not a zero-length bar — an empty track with a label, so a
                  // persona nobody has captured for doesn't read as one that
                  // scored zero.
                  <span className="persona__none">Not measured</span>
                ) : (
                  <span
                    className={`persona__fill persona__fill--${row.severity}`}
                    style={{ width: `${Math.max(2, row.mptr ?? 0)}%` }}
                  />
                )}
              </span>
              <span className="persona__value">
                {disabled ? "—" : `${row.mptr}%`}
              </span>
              <span className="persona__meta">
                {disabled ? "" : `NDI ${row.ndi} · ${row.samples}`}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
